import React, { useState } from 'react';
import { CreditCard, Lock, XCircle, CheckCircle, FlaskConical } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { parseLocalDate } from '../utils/helpers';
import { app } from '../config/firebase';
import { useTestMode } from '../hooks/useTestMode';

// Chiama loadStripe solo se la chiave pubblica è configurata — altrimenti Stripe.js
// lancia un IntegrationError non catturabile al semplice caricamento del modulo,
// anche su pagine che non usano affatto i pagamenti (es. la registrazione).
// Finché nessuna delle due chiavi (live/test) è configurata, stripePromise resta
// null e i componenti pagamento lo gestiscono.
//
// Cache per modalità + account Connect: loadStripe va chiamato una volta sola
// per combinazione — qui manteniamo una Promise separata per ogni coppia
// chiave/account, così passare da una modalità all'altra (interruttore nel
// Platform Admin Panel) o da un autonoleggio all'altro non ricrea
// continuamente lo script Stripe.js, e ogni componente pagamento riceve
// sempre la Promise coerente con la modalità e l'account attivi in quel momento.
//
// FIX (Settembre 2026): il PaymentIntent viene creato con Direct Charge
// DIRETTAMENTE sull'account Connect dell'autonoleggio (vedi createPaymentIntent,
// { stripeAccount: stripeConnectAccountId }). Stripe.js deve sapere su quale
// account Connect cercarlo, altrimenti lo cerca sull'account piattaforma e
// fallisce con "No such payment_intent: pi_...". Per questo loadStripe ora
// riceve anche { stripeAccount }, e va richiamato solo DOPO la risposta di
// createPaymentIntent — prima non conosciamo ancora quell'account.
const stripePromiseCache = {};
function getStripePromise(testMode, stripeAccount) {
  const key = testMode ? process.env.REACT_APP_STRIPE_PUBLIC_KEY_TEST : process.env.REACT_APP_STRIPE_PUBLIC_KEY;
  if (!key || !stripeAccount) return null;
  const cacheKey = `${key}|${stripeAccount}`;
  if (!stripePromiseCache[cacheKey]) {
    stripePromiseCache[cacheKey] = loadStripe(key, { stripeAccount });
  }
  return stripePromiseCache[cacheKey];
}

const ELEMENT_OPTIONS = { style: { base: { fontSize: '16px', color: '#374151', fontFamily: 'inherit', '::placeholder': { color: '#9CA3AF' } }, invalid: { color: '#EF4444' } } };
const functions = getFunctions(app, 'europe-west1');
const createPaymentIntentFn = httpsCallable(functions, 'createPaymentIntent');
const BLU = '#2563EB';

const PaymentFormInner = ({ amount, onSuccess, onCancel, bookingDetails, testMode, clientSecret }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardName, setCardName] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [succeeded, setSucceeded] = useState(false);

  const translateStripeError = (err) => {
    const map = { card_declined: 'Carta rifiutata. Contatta la tua banca.', insufficient_funds: 'Fondi insufficienti.', expired_card: 'Carta scaduta.', incorrect_cvc: 'CVV non corretto.', processing_error: 'Errore elaborazione. Riprova tra poco.', incorrect_number: 'Numero carta non valido.' };
    return map[err.code] || err.message || 'Pagamento rifiutato.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    if (!stripe || !elements) { setError('Stripe non ancora caricato. Riprova tra un momento.'); return; }
    if (!cardName.trim()) { setError('Inserisci il nome del titolare della carta.'); return; }
    // clientSecret arriva già pronto dal componente esterno (creato PRIMA di
    // montare Elements, così Elements nasce già collegato all'account Connect
    // giusto — vedi commento su getStripePromise).
    if (!clientSecret) { setError('Impossibile avviare il pagamento. Ricarica la pagina e riprova.'); return; }
    setProcessing(true);
    try {
      const cardElement = elements.getElement(CardNumberElement);
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, { payment_method: { card: cardElement, billing_details: { name: cardName.trim() } } });
      if (stripeError) { setError(translateStripeError(stripeError)); setProcessing(false); return; }
      if (paymentIntent.status === 'succeeded') {
        setSucceeded(true);
        setTimeout(() => { onSuccess({ transactionId: paymentIntent.id, paymentMethod: 'card_****' + (paymentIntent.payment_method?.card?.last4 || '????'), amount, currency: 'EUR', timestamp: new Date().toISOString() }); }, 1500);
      }
    } catch (err) {
      if (err.code === 'functions/internal') setError('Errore interno del server. Controlla i log di Firebase.');
      else if (err.code === 'functions/unauthenticated') setError('Devi essere autenticato per effettuare il pagamento.');
      else setError(err.message || 'Errore imprevisto. Riprova.');
      setProcessing(false);
    }
  };

  if (succeeded) return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[70]">
      <div className="bg-white rounded-2xl border border-slate-100 max-w-md w-full p-10 text-center">
        <div className="w-16 h-16 bg-green-50 border border-green-200 rounded-2xl flex items-center justify-center mx-auto mb-4"><CheckCircle className="w-9 h-9 text-green-500" /></div>
        <h2 className="text-xl font-black text-slate-800 tracking-tight mb-2">Pagamento riuscito!</h2>
        <p className="text-slate-400 text-sm">Reindirizzamento in corso...</p>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end sm:items-center justify-center p-0 sm:p-4 z-[70]">
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl shadow-lg max-h-[90vh] overflow-y-auto rounded-t-2xl">
        <div className="rounded-t-2xl px-6 py-5 flex items-center gap-4" style={{ background: BLU }}>
          <div className="w-11 h-11 bg-white bg-opacity-10 rounded-xl flex items-center justify-center flex-shrink-0"><CreditCard className="w-5 h-5 text-white" /></div>
          <div><h2 className="text-lg font-black text-white tracking-tight">Pagamento Sicuro</h2><p className="text-xs text-white opacity-60">Alimentato da Stripe</p></div>
        </div>
        {testMode && (
          <div className="flex items-center gap-2 px-6 py-2.5 bg-amber-50 border-b border-amber-200 text-amber-800 text-xs font-bold">
            <FlaskConical className="w-3.5 h-3.5 shrink-0" />
            MODALITÀ TEST — nessun addebito reale. Usa la carta 4242 4242 4242 4242.
          </div>
        )}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-5 p-4 bg-slate-50 rounded-2xl border border-slate-200">
            <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3">Riepilogo Prenotazione</p>
            <div className="space-y-1.5 text-sm text-slate-600">
              <div className="flex justify-between"><span>Auto</span><span className="font-bold text-slate-800">{bookingDetails?.carName}</span></div>
              <div className="flex justify-between"><span>Dal</span><span className="font-medium">{bookingDetails?.dataInizio ? parseLocalDate(bookingDetails.dataInizio).toLocaleDateString('it-IT') : '—'}</span></div>
              <div className="flex justify-between"><span>Al</span><span className="font-medium">{bookingDetails?.dataFine ? parseLocalDate(bookingDetails.dataFine).toLocaleDateString('it-IT') : '—'}</span></div>
              <div className="flex justify-between"><span>Durata</span><span className="font-medium">{bookingDetails?.giorni} giorni</span></div>
              {bookingDetails?.luogoRitiroLabel && <div className="flex justify-between"><span>Ritiro</span><span className="font-medium text-right">{bookingDetails.luogoRitiroLabel}</span></div>}
              {bookingDetails?.luogoRiconsegnaLabel && <div className="flex justify-between"><span>Riconsegna</span><span className="font-medium text-right">{bookingDetails.luogoRiconsegnaLabel}</span></div>}
              {bookingDetails?.discountCode && <div className="flex justify-between text-green-600"><span>Sconto</span><span className="font-medium">-€{bookingDetails.discountAmount?.toFixed(2)}</span></div>}
              {bookingDetails?.supplementoRitiro > 0 && <div className="flex justify-between"><span>Supplemento ritiro</span><span className="font-medium">+€{bookingDetails.supplementoRitiro.toFixed(2)}</span></div>}
              {bookingDetails?.supplementoRiconsegna > 0 && <div className="flex justify-between"><span>Supplemento riconsegna</span><span className="font-medium">+€{bookingDetails.supplementoRiconsegna.toFixed(2)}</span></div>}
            </div>
            <div className="mt-3 pt-3 border-t border-slate-200 flex justify-between items-center">
              <span className="font-bold text-slate-700">Totale:</span>
              <span className="text-2xl font-black tracking-tight" style={{ color: BLU }}>€{amount}</span>
            </div>
          </div>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 flex items-start gap-2">
              <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-500" /><span>{error}</span>
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2">Nome Titolare</label>
              <input type="text" value={cardName} onChange={(e) => setCardName(e.target.value.toUpperCase())} placeholder="MARIO ROSSI"
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 uppercase text-sm" disabled={processing} required />
            </div>
            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2">Numero Carta</label>
              <div className="w-full px-4 py-3 border border-slate-200 rounded-xl focus-within:ring-2 focus-within:ring-blue-600 focus-within:border-blue-600 bg-white transition"><CardNumberElement options={ELEMENT_OPTIONS} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2">Scadenza</label>
                <div className="w-full px-4 py-3 border border-slate-200 rounded-xl focus-within:ring-2 focus-within:ring-blue-600 bg-white transition"><CardExpiryElement options={ELEMENT_OPTIONS} /></div>
              </div>
              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2">CVV</label>
                <div className="w-full px-4 py-3 border border-slate-200 rounded-xl focus-within:ring-2 focus-within:ring-blue-600 bg-white transition"><CardCvcElement options={ELEMENT_OPTIONS} /></div>
              </div>
            </div>
          </div>
          <div className="mt-5 flex items-center gap-2 text-xs text-slate-500 p-3 bg-slate-50 border border-slate-200 rounded-xl">
            <Lock className="w-4 h-4 text-green-500 flex-shrink-0" />
            <span>Pagamento sicuro e crittografato · PCI DSS compliant</span>
          </div>
          <div className="mt-5 flex gap-3">
            <button type="submit" disabled={processing || !stripe} className="flex-1 py-3.5 rounded-xl font-black text-sm transition"
              style={!processing && stripe ? { background: BLU, color: '#fff' } : { background: '#E2E8F0', color: '#94A3B8', cursor: 'not-allowed' }}>
              {processing ? 'Elaborazione…' : `Paga €${amount}`}
            </button>
            <button type="button" onClick={onCancel} disabled={processing}
              className="px-5 py-3.5 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 transition font-bold text-sm disabled:opacity-50">
              Annulla
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const StripePaymentForm = (props) => {
  const { testMode, loaded } = useTestMode();
  const { amount, bookingDetails, onCancel } = props;
  const [paymentIntentData, setPaymentIntentData] = useState(null); // { clientSecret, stripeConnectAccountId }
  const [initError, setInitError] = useState('');

  // FIX (Agosto 2026): react-stripe-js NON permette di cambiare la prop
  // `stripe` di <Elements> dopo il primo render (lo ignora silenziosamente,
  // con un warning in console: "Unsupported prop change on Elements: You
  // cannot change the `stripe` prop after setting it.").
  //
  // Prima, <Elements> veniva montato subito con testMode preso da
  // useTestMode() — che all'inizio vale sempre false (stato di default di
  // useState), finché il listener realtime su platformSettings/testMode
  // non risponde (pochi millisecondi dopo, ma sempre DOPO il primo render).
  // Elements si "bloccava" quindi sulla chiave pubblica LIVE anche quando
  // la piattaforma era in modalità test — causando conferme di pagamento
  // (stripe.confirmCardPayment) con la chiave sbagliata rispetto a dove il
  // PaymentIntent era stato creato lato server (createPaymentIntent, che
  // legge testMode da un'altra fonte, sempre aggiornata: il DB via Admin
  // SDK). Da qui l'errore "No such payment_intent: ... a similar object
  // exists in test mode, but a live mode key was used".
  //
  // FIX (Settembre 2026): stesso principio, ma per l'account Connect.
  // createPaymentIntent crea il PaymentIntent DIRETTAMENTE sull'account
  // Stripe dell'autonoleggio (Direct Charge). <Elements> deve quindi essere
  // montato con loadStripe(pk, { stripeAccount }) fin dall'inizio, altrimenti
  // Stripe.js cerca il PaymentIntent sull'account piattaforma e fallisce con
  // "No such payment_intent". Per questo la Cloud Function viene chiamata
  // QUI, prima di montare <Elements>, invece che dentro PaymentFormInner al
  // click su "Paga": solo dopo questa risposta sappiamo su quale account
  // Connect inizializzare Stripe.js.
  React.useEffect(() => {
    if (!loaded || paymentIntentData) return;
    let cancelled = false;
    createPaymentIntentFn({
      amount: Math.round(amount * 100), // mantenuto solo come riferimento nei log server; l'addebito reale usa il prezzo ricalcolato lato server
      bookingId: bookingDetails?.bookingId || null,
      companyId: bookingDetails?.companyId || null,
      carId: bookingDetails?.carId || null,
      dataInizio: bookingDetails?.dataInizio || null,
      dataFine: bookingDetails?.dataFine || null,
      discountCode: bookingDetails?.discountCode || null,
      // Solo gli id: nome, indirizzo e supplemento li rilegge il server dal database.
      puntoRitiroId: bookingDetails?.puntoRitiroId || null,
      puntoRiconsegnaId: bookingDetails?.puntoRiconsegnaId || null,
      description: `Noleggio: ${bookingDetails?.carName || 'Auto'} — ${bookingDetails?.giorni || '?'} giorni`,
    }).then((result) => {
      if (cancelled) return;
      const { clientSecret, stripeConnectAccountId } = result.data || {};
      if (!clientSecret || !stripeConnectAccountId) {
        setInitError('Impossibile avviare il pagamento. Riprova tra un momento.');
        return;
      }
      setPaymentIntentData({ clientSecret, stripeConnectAccountId });
    }).catch((err) => {
      if (cancelled) return;
      if (err.code === 'functions/unauthenticated') setInitError('Devi essere autenticato per effettuare il pagamento.');
      else if (err.code === 'functions/failed-precondition' && err.details?.code === 'punto-non-disponibile' && props.onPointUnavailable) {
        // Punto disattivato/eliminato prima del pagamento: il modal riparte dalla scelta con le opzioni riaggiornate.
        props.onPointUnavailable(err.message);
      }
      else setInitError(err.message || 'Impossibile avviare il pagamento. Riprova.');
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  if (!loaded || (!paymentIntentData && !initError)) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[70]">
        <div className="bg-white rounded-2xl border border-slate-100 max-w-md w-full p-10 text-center">
          <div className="w-8 h-8 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Caricamento modulo di pagamento...</p>
        </div>
      </div>
    );
  }

  if (initError) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[70]">
        <div className="bg-white rounded-2xl border border-slate-100 max-w-md w-full p-10 text-center">
          <XCircle className="w-9 h-9 text-red-500 mx-auto mb-3" />
          <p className="text-slate-700 text-sm font-semibold mb-1">Impossibile avviare il pagamento</p>
          <p className="text-slate-400 text-sm mb-5">{initError}</p>
          <button type="button" onClick={onCancel} className="px-5 py-2.5 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 transition font-bold text-sm">Chiudi</button>
        </div>
      </div>
    );
  }

  const stripePromise = getStripePromise(testMode, paymentIntentData.stripeConnectAccountId);
  return (
    <Elements stripe={stripePromise}>
      <PaymentFormInner {...props} testMode={testMode} clientSecret={paymentIntentData.clientSecret} />
    </Elements>
  );
};

export default StripePaymentForm;
